import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import selectors from '@/redux/selectors/selectors';
import { workflowLogsMetadata } from '@/redux/selectors/workflowSelectors';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import Button from '@/components/Button';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import useWorkflowLogsFilters from '@/components/modals/workflowLogsModal/lib/useWorkflowLogsFilters';
import ensureSelectedFilterOptions from '@/components/modals/workflowLogsModal/lib/ensureSelectedFilterOptions';
import { FiActivity } from '@/components/react-icons/main/index.esm';

const ActivityFilter = () => {
  const workflowId = useArgSelector(selectors.workflowId);
  const activityFilter = useArgSelector(selectors.pref, `workflowLogsActivityFilter-${workflowId}`);
  const workflowLogsMetadataData = useSelector(workflowLogsMetadata);
  const { handleFilterChange } = useWorkflowLogsFilters();

  const hasActivityFilter = Array.isArray(activityFilter) ? activityFilter.length > 0 : !!activityFilter;

  const activityOptions = useMemo(() => {
    const totalActivityMetadata = workflowLogsMetadataData?.find((meta) => meta.type === 'total_activity');
    const totalActivityCount = totalActivityMetadata?.totalCount || 0;

    const operationsMetadata = workflowLogsMetadataData?.filter((meta) => meta.type === 'operations') || [];

    const errorCount = +operationsMetadata.find((meta) => meta.id === 'error')?.totalCount || 0;
    const warningCount = +operationsMetadata.find((meta) => meta.id === 'warning')?.totalCount || 0;
    const infoCount = +operationsMetadata.find((meta) => meta.id === 'info')?.totalCount || 0;
    const successCount = +operationsMetadata.find((meta) => meta.id === 'success')?.totalCount || 0;

    const options = [];

    if (totalActivityCount > 0) {
      options.push({ label: 'Logs with Activity', value: 'hasActivity', totalCount: totalActivityCount });
    }
    if (errorCount > 0) {
      options.push({ label: 'Error', value: 'error', totalCount: errorCount });
    }
    if (warningCount > 0) {
      options.push({ label: 'Warning', value: 'warning', totalCount: warningCount });
    }
    if (infoCount > 0) {
      options.push({ label: 'Info', value: 'info', totalCount: infoCount });
    }
    if (successCount > 0) {
      options.push({ label: 'Success', value: 'success', totalCount: successCount });
    }
    return ensureSelectedFilterOptions({
      options,
      selectedValues: activityFilter,
      getSelectedOption: (value) => {
        const labelsByValue = {
          hasActivity: 'Logs with Activity',
          error: 'Error',
          warning: 'Warning',
          info: 'Info',
          success: 'Success'
        };

        return { label: labelsByValue[value] || value };
      }
    });
  }, [workflowLogsMetadataData, activityFilter]);

  const handleActivityChange = (value) => {
    handleFilterChange('activity', value);
  };

  return (
    <>
      <Button className={`outline filter activity-filter ${hasActivityFilter ? 'active' : ''}`} size="sm" icon={FiActivity}>
        Activity
      </Button>
      <FloatingSelectV2 multiple triggers={[`.activity-filter`]} options={activityOptions} onChange={handleActivityChange} useSearch={false} value={activityFilter} placement="bottom" offset={4} />
    </>
  );
};

export default ActivityFilter;
